import { ProgramStatus } from "@humanex/interfaces";
import { Box, Button, TiptapView } from "@humanex/ui/components";
import { useI18n } from "@humanex/ui/libs";
import { createFileRoute, useNavigate, useParams, useRouteContext } from "@tanstack/react-router";

const EDITABLE_STATUSES = [
  ProgramStatus.DRAFT,
  ProgramStatus.REJECTED,
  ProgramStatus.PUBLISHED,
  ProgramStatus.PAUSED
];

export const Route = createFileRoute("/_authenticated/org/$orgId/program/$programId/")({
  component: RouteComponent
});

const PROGRAM_TYPE_LABELS: Record<string, string> = {
  DU_HOC: "Du học",
  VIEC_LAM: "Việc làm",
  XUAT_KHAU: "Xuất khẩu lao động",
  HOC_NGHE: "Học nghề"
};

const PROGRAM_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Nháp",
  PENDING_REVIEW: "Chờ duyệt",
  REJECTED: "Bị từ chối",
  VERIFIED: "Đã duyệt",
  PUBLISHED: "Đang mở",
  CLOSED: "Đã đóng",
  PAUSED: "Tạm dừng"
};

function RouteComponent() {
  const { orgId, programId } = useParams({
    from: "/_authenticated/org/$orgId/program/$programId/"
  });
  const { program } = useRouteContext({
    from: "/_authenticated/org/$orgId/program/$programId/"
  });
  const navigate = useNavigate();
  const { formatDate } = useI18n();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-2">
          <p className="text-responsive-xl font-normal text-text-primary">{program.title}</p>
          <div className="flex gap-2 flex-wrap">
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
              {PROGRAM_TYPE_LABELS[program.type] ?? program.type}
            </span>
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                program.status === ProgramStatus.PUBLISHED
                  ? "bg-success-100 text-success-700"
                  : program.status === ProgramStatus.PAUSED
                    ? "bg-warning-100 text-warning-700"
                    : "bg-gray-100 text-gray-600"
              }`}
            >
              {PROGRAM_STATUS_LABELS[program.status] ?? program.status}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: "/org/$orgId/program/$programId/applicants",
                params: { orgId, programId }
              })
            }
          >
            Ứng viên
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: "/org/$orgId/program/$programId/questions",
                params: { orgId, programId }
              })
            }
          >
            Câu hỏi bổ sung
          </Button>
          {EDITABLE_STATUSES.includes(program.status) && (
            <Button
              variant="default"
              onClick={() =>
                navigate({
                  to: "/org/$orgId/program/$programId/edit",
                  params: { orgId, programId }
                })
              }
            >
              Chỉnh sửa
            </Button>
          )}
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <Box className="space-y-3">
            <p className="text-responsive-md font-medium text-text-primary">Mô tả</p>
            <TiptapView content={program.description} variant="compact" />
          </Box>
          {program.requirements && (
            <Box className="space-y-3">
              <p className="text-responsive-md font-medium text-text-primary">Yêu cầu ứng viên</p>
              <TiptapView content={program.requirements} variant="compact" />
            </Box>
          )}
          {program.benefits && (
            <Box className="space-y-3">
              <p className="text-responsive-md font-medium text-text-primary">Quyền lợi</p>
              <TiptapView content={program.benefits} variant="compact" />
            </Box>
          )}
        </div>

        {/* Sidebar */}
        <Box className="space-y-4 h-fit">
          <p className="text-responsive-md font-medium text-text-primary">Thông tin chi tiết</p>
          <div className="space-y-3">
            <DetailRow label="Loại chương trình" value={PROGRAM_TYPE_LABELS[program.type] ?? program.type} />
            <DetailRow label="Trạng thái" value={PROGRAM_STATUS_LABELS[program.status] ?? program.status} />
            <DetailRow label="Chỉ tiêu tuyển" value={String(program.quota)} />
            {program.country && <DetailRow label="Quốc gia" value={program.country} />}
            {program.location && <DetailRow label="Địa điểm" value={program.location} />}
            <DetailRow
              label="Hạn nộp hồ sơ"
              value={
                program.deadline
                  ? formatDate(program.deadline, {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })
                  : "Không giới hạn"
              }
            />
            <DetailRow label="Phí đặt cọc" value={`${program.depositAmount.toLocaleString("vi-VN")} VNĐ`} />
            <DetailRow label="Chi phí" value={program.costVisible ? "Công khai" : "Ẩn"} />
          </div>
        </Box>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 text-sm">
      <span className="text-text-secondary shrink-0">{label}</span>
      <span className="text-text-primary font-medium text-right">{value}</span>
    </div>
  );
}
